/**
 * Trang Hồ sơ cá nhân – thông tin tài khoản, vai trò và quyền truy cập.
 */
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Person, Badge, Info } from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'

const ROLE_LABELS: Record<string, string> = {
  user: 'Đương sự',
  student: 'Sinh viên',
  litigant: 'Đương sự',
  secretary: 'Thư ký',
  judge: 'Thẩm phán',
  admin: 'Chánh án',
  chief_judge: 'Chánh án',
  support: 'Hỗ trợ',
  coach: 'Huấn luyện viên',
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  user: 'Nộp đơn khởi kiện, theo dõi vụ án, nhận văn bản tố tụng, xác nhận đã nhận, xin hoãn phiên, nộp kháng cáo.',
  litigant: 'Nộp đơn khởi kiện, theo dõi vụ án, nhận văn bản tố tụng, xác nhận đã nhận, xin hoãn phiên, nộp kháng cáo.',
  secretary: 'Xử lý sơ bộ đơn, kiểm tra hình thức, gửi giấy báo, quản lý lịch phiên, soạn thảo và ký văn bản.',
  judge: 'Phân công án, duyệt văn bản, lên lịch phiên, quản lý chứng cứ, tham gia phiên tòa trực tuyến.',
  admin: 'Quyết định thẩm quyền (thụ lý/trả đơn/chuyển đơn), phân công Thẩm phán, ký văn bản, phê duyệt chứng cứ.',
  chief_judge: 'Quyết định thẩm quyền (thụ lý/trả đơn/chuyển đơn), phân công Thẩm phán, ký văn bản, phê duyệt chứng cứ.',
}

export default function ProfilePage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useAppSelector((state) => state.auth)

  if (!user) {
    return null
  }

  const displayName = user.full_name || user.username || '—'
  const initial = (displayName !== '—' ? displayName : user.username || 'U').charAt(0).toUpperCase()
  const roleLabel = ROLE_LABELS[user.role] ?? user.role
  const roleDesc = ROLE_DESCRIPTIONS[user.role] ?? ''

  return (
    <Box sx={{ width: '100%', maxWidth: 640, mx: 'auto' }}>
      <Typography
        variant="h5"
        fontWeight={600}
        gutterBottom
        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }, mb: 1 }}
      >
        Hồ sơ cá nhân
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
        Tài khoản và quyền truy cập trong hệ thống Tòa án điện tử
      </Typography>

      <Card sx={{ borderRadius: 2, boxShadow: 1, mb: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            py: 3,
            px: 2,
            bgcolor: 'rgba(26, 43, 74, 0.06)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Avatar
            sx={{
              width: isMobile ? 72 : 88,
              height: isMobile ? 72 : 88,
              bgcolor: 'primary.main',
              fontSize: isMobile ? '1.75rem' : '2rem',
              mb: 1.5,
            }}
          >
            {initial}
          </Avatar>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, mb: 0.5 }}
          >
            {displayName}
          </Typography>
          <Chip
            label={roleLabel}
            size="small"
            icon={<Badge sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 600,
              fontSize: '0.8125rem',
            }}
          />
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <Info color="action" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
              Thông tin tài khoản
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <InfoRow label="Tên đăng nhập" value={user.username} />
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <InfoRow label="Email" value={user.email} />
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <InfoRow label="Họ và tên" value={user.full_name || '—'} />
            <Divider flexItem sx={{ borderStyle: 'dashed' }} />
            <InfoRow label="Vai trò" value={roleLabel} />
          </Box>
        </CardContent>
      </Card>

      {roleDesc && (
        <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <Person color="action" sx={{ fontSize: 20 }} />
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.8125rem' }}>
                Quyền truy cập theo vai trò
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
              {roleDesc}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
        {value}
      </Typography>
    </Box>
  )
}
